import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../features/auth/AuthProvider.js";
import { ApiClientError } from "../services/api-client.js";
import * as inventoryApi from "../services/inventory-api.js";
import type { InventoryBalance, InventoryMovement, Product, Warehouse } from "../types/api.js";

function isPlatformRole(role: string): boolean {
  return role === "superadmin" || role === "platform-support";
}

export function InventoryPage(): JSX.Element {
  const { token, user } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [balances, setBalances] = useState<InventoryBalance[]>([]);
  const [movements, setMovements] = useState<InventoryMovement[]>([]);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "ok" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [skuFilter, setSkuFilter] = useState("");
  const [warehouseFilter, setWarehouseFilter] = useState("");

  const tenantQuery = useMemo(() => {
    if (user?.tenantId) return { tenantId: user.tenantId };
    return {};
  }, [user?.tenantId]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoadState("loading");
    setErrorMsg(null);
    try {
      const skuQ = skuFilter.trim() || undefined;
      const whQ = warehouseFilter || undefined;
      const balParams = {
        ...tenantQuery,
        sku: skuQ,
        warehouseId: whQ,
      };
      const movParams = { ...tenantQuery };

      const [p, w, b, m] = await Promise.all([
        inventoryApi.fetchProducts(token, { ...tenantQuery, sku: skuQ }),
        inventoryApi.fetchWarehouses(token, tenantQuery),
        inventoryApi.fetchInventoryBalances(token, balParams),
        inventoryApi.fetchInventoryMovements(token, movParams),
      ]);
      setProducts(p.products);
      setWarehouses(w.warehouses);
      setBalances(b.balances);
      setMovements(m.movements.slice(0, 40));
      setLoadState("ok");
    } catch (e) {
      setLoadState("error");
      setErrorMsg(e instanceof ApiClientError ? e.message : "No se pudo cargar el inventario.");
    }
  }, [token, tenantQuery, skuFilter, warehouseFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const totalFree = useMemo(
    () => balances.reduce((s, r) => s + Number(r.quantity || 0), 0),
    [balances],
  );
  const totalReserved = useMemo(
    () => balances.reduce((s, r) => s + Number(r.reservedQuantity || 0), 0),
    [balances],
  );
  const totalOnHand = useMemo(
    () =>
      balances.reduce((s, r) => {
        if (r.totalOnHandQuantity != null && r.totalOnHandQuantity !== "") return s + Number(r.totalOnHandQuantity);
        return s + Number(r.quantity || 0) + Number(r.reservedQuantity || 0);
      }, 0),
    [balances],
  );

  return (
    <div className="inventory-page">
      <header className="page-header">
        <h1>Inventario</h1>
        <p className="text-muted">
          Datos en vivo desde la API (Fase 4.1): productos, almacenes, balances y movimientos recientes.
        </p>
        {user && isPlatformRole(user.role) && !user.tenantId ? (
          <p className="banner-inline" role="note">
            Su sesión es de <strong>plataforma</strong> sin tenant asignado: ve todos los tenants en listados. Para una
            vista solo tenant, use un usuario con <code>tenantId</code> (p. ej. <code>admin@acme.trace</code>).
          </p>
        ) : null}
      </header>

      <section className="card filters-card">
        <h2 className="section-title">Filtros</h2>
        <div className="filters-row">
          <label className="field-inline">
            <span>SKU contiene</span>
            <input
              type="text"
              value={skuFilter}
              onChange={(e) => setSkuFilter(e.target.value)}
              placeholder="p. ej. DEMO"
            />
          </label>
          <label className="field-inline">
            <span>Almacén</span>
            <select value={warehouseFilter} onChange={(e) => setWarehouseFilter(e.target.value)}>
              <option value="">Todos</option>
              {warehouses.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.code} — {w.name}
                </option>
              ))}
            </select>
          </label>
          <button type="button" className="btn btn-secondary" onClick={() => void load()} disabled={loadState === "loading"}>
            {loadState === "loading" ? "Cargando…" : "Aplicar"}
          </button>
        </div>
      </section>

      {loadState === "loading" ? <p className="text-muted">Cargando datos de inventario…</p> : null}
      {loadState === "error" ? <p className="form-error">{errorMsg}</p> : null}

      {loadState === "ok" ? (
        <>
          <section className="card-grid summary-grid">
            <article className="card stat-card">
              <h3>Productos (catálogo)</h3>
              <p className="stat-value">{products.length}</p>
              <p className="text-muted small">Filas devueltas por <code>GET /api/v1/products</code></p>
            </article>
            <article className="card stat-card">
              <h3>Almacenes</h3>
              <p className="stat-value">{warehouses.length}</p>
            </article>
            <article className="card stat-card">
              <h3>Stock en almacén (suma tuplas)</h3>
              <p className="stat-value">{totalOnHand.toFixed(2)}</p>
              <p className="text-muted small">
                Libre: {totalFree.toFixed(2)} · Reservado: {totalReserved.toFixed(2)}
              </p>
            </article>
          </section>

          <section className="card table-section">
            <h2 className="section-title">Balances de stock</h2>
            {balances.length === 0 ? (
              <p className="text-muted empty-hint">No hay balances con los filtros actuales.</p>
            ) : (
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>SKU</th>
                      <th>Producto</th>
                      <th>Almacén</th>
                      <th>Ubicación</th>
                      <th className="num">Libre</th>
                      <th className="num">Reservado</th>
                      <th className="num">Total</th>
                      <th>Actualizado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {balances.map((r) => (
                      <tr key={r.id}>
                        <td>
                          <code>{r.productSku}</code>
                        </td>
                        <td>{r.productName}</td>
                        <td>
                          <code>{r.warehouseCode}</code>
                        </td>
                        <td>{r.locationCode ?? <span className="text-muted">—</span>}</td>
                        <td className="num">{r.availableQuantity ?? r.quantity}</td>
                        <td className="num">{r.reservedQuantity}</td>
                        <td className="num">
                          {r.totalOnHandQuantity ??
                            (Number(r.quantity) + Number(r.reservedQuantity)).toFixed(4)}
                        </td>
                        <td className="nowrap">{new Date(r.updatedAt).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="card table-section">
            <h2 className="section-title">Movimientos recientes</h2>
            <p className="text-muted small">Hasta 40 filas desde <code>GET /api/v1/inventory/movements</code> (el servidor limita a 500).</p>
            {movements.length === 0 ? (
              <p className="text-muted empty-hint">Sin movimientos visibles para su alcance.</p>
            ) : (
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Tipo</th>
                      <th>Cantidad</th>
                      <th>Ref.</th>
                      <th>Notas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movements.map((mov) => (
                      <tr key={mov.id}>
                        <td className="nowrap">{new Date(mov.createdAt).toLocaleString()}</td>
                        <td>
                          <code>{mov.type}</code>
                        </td>
                        <td className="num">{mov.quantity}</td>
                        <td>{mov.reference ?? "—"}</td>
                        <td className="truncate">{mov.notes ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="card gaps-card">
            {/* Documentación oficial: https://github.com/jowy81/0xvera-trace-docs/blob/main/docs/00-overview/01-product.md#inventario */}
            <h2 className="section-title">Alcance y próximas fases</h2>
            <p className="text-muted small">
              <a
                href="https://github.com/jowy81/0xvera-trace-docs/blob/main/docs/00-overview/01-product.md#inventario"
                target="_blank"
                rel="noreferrer"
              >
                Documentación oficial (inventario)
              </a>
            </p>
          </section>
        </>
      ) : null}
    </div>
  );
}
